import { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper/modules';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

interface ImageGalleryProps {
  images: string[];
  name: string;
}

// Default: pull un-hashed images from the legacy chateaushi.com/images path,
// which web-2 now serves both via its API server and the static SPA deploy.
// For local preview without internet, set VITE_IMAGE_BASE_URL=http://localhost:5001/images
// (pointing at the local web-2 server) or /images_project (the public symlink).
const IMAGE_BASE_URL =
  import.meta.env.VITE_IMAGE_BASE_URL || 'https://chateaushi.com/images';

export function ImageGallery({ images, name }: ImageGalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  const getImageUrl = (path: string) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    // JSON stores paths like "foo.jpg" or "previews/foo.jpg" without a leading slash.
    return `${IMAGE_BASE_URL}/${path.replace(/^\/+/, '')}`;
  };

  if (images.length === 0) {
    return (
      <div className="aspect-[16/10] bg-white/5 flex items-center justify-center">
        <span className="text-slate-400">No images available</span>
      </div>
    );
  }

  return (
    <div className="relative group">
      <Swiper
        modules={[Pagination, Navigation]}
        pagination={{ clickable: true }}
        navigation={{
          prevEl: '.gallery-prev',
          nextEl: '.gallery-next',
        }}
        onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
        className="aspect-[16/10] bg-white/5"
      >
        {images.map((image, index) => (
          <SwiperSlide key={index}>
            <img
              src={getImageUrl(image)}
              alt={`${name} - Image ${index + 1}`}
              className="w-full h-full object-cover"
              loading={index === 0 ? 'eager' : 'lazy'}
            />
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Custom navigation */}
      {images.length > 1 && (
        <>
          <button className="gallery-prev absolute left-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-black/50 backdrop-blur-sm hover:bg-black/70 rounded-full flex items-center justify-center border border-white/20 transition-all">
            <ChevronLeft className="w-5 h-5 text-foreground" />
          </button>
          <button className="gallery-next absolute right-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-black/50 backdrop-blur-sm hover:bg-black/70 rounded-full flex items-center justify-center border border-white/20 transition-all">
            <ChevronRight className="w-5 h-5 text-foreground" />
          </button>
        </>
      )}

      {/* Image counter */}
      <div className="absolute bottom-4 right-4 bg-black/50 backdrop-blur-sm text-foreground text-xs uppercase tracking-widest px-3 py-1 rounded-full border border-white/10">
        {activeIndex + 1} / {images.length}
      </div>
    </div>
  );
}
